import React from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { format as fnsFormat, parseISO, isValid } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

/**
 * French date picker (jj/mm/aaaa). Stores ISO yyyy-MM-dd in state, displays
 * French long date inside the button. Designed as a drop-in replacement for
 * <Input type="date" value={value} onChange={(e) => setX(e.target.value)} />.
 *
 * Props:
 *   value:       string  ISO yyyy-MM-dd  (or empty)
 *   onChange:    fn(iso) called with ISO yyyy-MM-dd  (or "" if cleared)
 *   placeholder: string  fallback label when value is empty
 *   testId:      string  data-testid for the trigger button
 *   disabled:    bool
 *   className:   string  extra classes for the trigger button
 */
export function DatePicker({
  value,
  onChange,
  placeholder = "Sélectionner une date",
  testId,
  disabled = false,
  className = "",
}) {
  const [open, setOpen] = React.useState(false);
  const date = React.useMemo(() => {
    if (!value) return undefined;
    try {
      const d = typeof value === "string" ? parseISO(value) : value;
      return isValid(d) ? d : undefined;
    } catch (_) {
      return undefined;
    }
  }, [value]);

  const display = date ? fnsFormat(date, "dd/MM/yyyy", { locale: fr }) : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "w-full justify-start text-left font-normal h-10",
            !date && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {display}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          locale={fr}
          weekStartsOn={1}
          selected={date}
          onSelect={(d) => {
            if (!d) {
              onChange?.("");
              setOpen(false);
              return;
            }
            // Build yyyy-MM-dd in local time to avoid TZ off-by-one
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            const dd = String(d.getDate()).padStart(2, "0");
            onChange?.(`${yyyy}-${mm}-${dd}`);
            setOpen(false);
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export default DatePicker;
